/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { SlugService } from '../../services/slug.service';

/**
 * Component for editing URL slugs with automatic generation and manual override
 *
 * Features:
 * - Automatically generates slugs from source text (e.g., article title)
 * - Allows manual editing with visual feedback
 * - Shows warning when slug differs from original (breaks old links)
 * - Provides reset to original and reset to automatic buttons
 * - Keyboard accessible (Enter/Space for reset button)
 */
@Component({
  selector: 'ut-slug-input',
  templateUrl: './slug-input.component.html',
  styleUrls: ['./slug-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SlugInputComponent implements OnInit, OnChanges {
  /**
   * URL prefix to display before the slug (e.g., "https://www.utest.com/articles/")
   */
  @Input() urlPre: string = '';

  /**
   * Source text for automatic slug generation (e.g., article title)
   */
  @Input() toSlug: string = '';

  /**
   * Current slug value
   */
  @Input() theSlug: string = '';

  /**
   * Error state flag for validation
   */
  @Input() hasError: boolean = false;

  /**
   * DOM id for the slug text input, so a page's label and error link can point at it
   */
  @Input() inputId: string | null = null;

  /** Id of the visible error text, so the input can reference it while `hasError` is set. */
  @Input() errorId: string | null = null;

  /**
   * Text shown in place of the field until there is a title to build the URL from
   */
  @Input() emptyText: string = '';

  /**
   * Event emitted when slug changes
   */
  @Output() slugChange = new EventEmitter<string>();

  /**
   * Whether manual edit mode is active
   */
  editMode: boolean = false;

  /**
   * Original slug value (stored on init)
   */
  originalSlug: string = '';

  /**
   * Whether to show warning about breaking old links
   */
  changeAlert: boolean = false;

  /**
   * Working copy of the slug value for internal manipulation
   */
  currentSlug: string = '';

  /**
   * Flag to track if automatic slug generation is active
   * (slug matches what would be generated from toSlug)
   */
  public isAutomaticSlugActive: boolean = false;

  constructor(
    private slugService: SlugService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Store the original slug for reset functionality
    this.originalSlug = this.theSlug;
    this.currentSlug = this.theSlug;

    // Determine if we're in edit mode (slug differs from auto-generated)
    const autoSlug = this.slugService.slugify(this.toSlug);
    this.editMode = this.theSlug !== autoSlug;

    // Check if slug exactly matches toSlug (not auto-generated)
    this.isAutomaticSlugActive = this.toSlug === this.theSlug;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Auto-update slug when toSlug changes (only if in automatic mode)
    if (changes.toSlug && !changes.toSlug.firstChange) {
      if (this.isAutomaticSlugActive && !this.editMode) {
        const newSlug = this.slugService.slugify(this.toSlug);
        this.currentSlug = newSlug;
        this.updateSlug(newSlug);

        // Show warning if changed from original
        this.changeAlert = (newSlug !== this.originalSlug && this.originalSlug !== '');
        this.cdr.markForCheck();
      }
    }

    // Update current slug if theSlug input changes
    if (changes.theSlug && !changes.theSlug.firstChange) {
      this.currentSlug = this.theSlug;
      this.cdr.markForCheck();
    }
  }

  /**
   * Activate edit mode (called on click or focus)
   */
  activateEditMode(): void {
    this.editMode = true;
    this.cdr.markForCheck();
  }

  /**
   * Handle manual slug input changes
   */
  onSlugInput(value: string): void {
    this.currentSlug = value;
    this.hasError = false;

    // Show change warning if original exists and slug differs
    if (this.originalSlug.length > 0) {
      this.changeAlert = value !== this.originalSlug;
    }

    // Update parent component
    this.updateSlug(value);

    // Check if we should still be in edit mode
    const autoSlug = this.slugService.slugify(this.toSlug);
    this.editMode = value !== autoSlug;

    this.cdr.markForCheck();
  }

  /**
   * Reset slug to original value
   */
  resetToOriginal(): void {
    this.changeAlert = false;
    this.editMode = false;
    this.currentSlug = this.originalSlug;
    this.updateSlug(this.originalSlug);

    // Check if original matches toSlug
    this.isAutomaticSlugActive = this.toSlug === this.originalSlug;

    this.cdr.markForCheck();
  }

  /**
   * Reset slug to automatic (regenerate from toSlug)
   */
  resetToAutomatic(): void {
    const newSlug = this.slugService.slugify(this.toSlug);
    this.currentSlug = newSlug;
    this.updateSlug(newSlug);

    // Show warning if different from original
    this.changeAlert = (newSlug !== this.originalSlug && !!this.originalSlug);

    this.editMode = false;
    this.isAutomaticSlugActive = true;

    this.cdr.markForCheck();
  }

  /**
   * Handle keyboard events on reset button (Enter/Space)
   */
  onResetKeydown(event: KeyboardEvent): void {
    const { key } = event;

    // Check for Enter or Space key
    if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
      event.preventDefault();
      this.resetToAutomatic();
    }
  }

  /**
   * Emit slug change event to parent component
   */
  private updateSlug(slug: string): void {
    this.slugChange.emit(slug);
  }

  /**
   * Check if input group should be visible
   */
  get shouldShowInput(): boolean {
    return !!(this.toSlug?.length || this.theSlug?.length);
  }
}
